import { useState, useEffect, useCallback, useRef } from 'react';
import { getUserPreferences, updateUserPreferences } from '../services/userPreferencesService';

export const TOUR_LS_KEY = 'terrio_tour_completed';
export const FEEDBACK_HINT_SESSION_KEY = 'terrio_show_feedback_hint';

export function useProductTour({
  isLoggedIn,
  authLoading,
  isMapRoute = true,
  closeNavDrawer,
  onTourFinished,
  onTourCheckResolved,
}) {
  const [run, setRun] = useState(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || authLoading || !isMapRoute) return;
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    const notifyNoTour = () => onTourCheckResolved?.({ showTour: false });

    try {
      if (localStorage.getItem(TOUR_LS_KEY) === 'true') {
        notifyNoTour();
        return;
      }
    } catch (_) {
      return;
    }

    getUserPreferences()
      .then((res) => {
        if (res.data?.onboarding_tour_completed) {
          try {
            localStorage.setItem(TOUR_LS_KEY, 'true');
          } catch (_) {
            // ignore
          }
          notifyNoTour();
          return;
        }

        onTourCheckResolved?.({ showTour: true });
        window.setTimeout(() => setRun(true), 800);
      })
      .catch(() => {
        notifyNoTour();
      });
  }, [isLoggedIn, authLoading, isMapRoute, onTourCheckResolved]);

  useEffect(() => {
    if (!isLoggedIn) {
      hasCheckedRef.current = false;
      setRun(false);
      closeNavDrawer?.();
    }
  }, [isLoggedIn, closeNavDrawer]);

  const persistTourCompletion = useCallback(async (skipped) => {
    setRun(false);
    closeNavDrawer?.();

    try {
      localStorage.setItem(TOUR_LS_KEY, 'true');
    } catch (_) {
      // ignore
    }

    try {
      await updateUserPreferences({
        onboarding_tour_completed: true,
        onboarding_tour_skipped: skipped,
      });
    } catch (e) {
      console.warn('Tour preference not saved to server', e);
    }

    onTourFinished?.();
  }, [onTourFinished, closeNavDrawer]);

  const handleCallback = useCallback(async (data) => {
    const { status } = data;

    if (status === 'finished' || status === 'skipped') {
      await persistTourCompletion(status === 'skipped');
    }
  }, [persistTourCompletion]);

  const restartTour = useCallback(async () => {
    try {
      await updateUserPreferences({ onboarding_tour_completed: false });
    } catch (e) {
      console.warn('Tour restart preference not saved to server', e);
    }

    try {
      localStorage.removeItem(TOUR_LS_KEY);
    } catch (_) {
      // ignore
    }

    hasCheckedRef.current = true;
    closeNavDrawer?.();
    setRun(false);
    window.setTimeout(() => setRun(true), 400);
  }, [closeNavDrawer]);

  return { run, handleCallback, restartTour };
}
